/**
 * Product Service
 * Responsabilidade: Cliente API para módulo de produtos
 * Princípios: Single Responsibility, separa lógica de API da lógica de domínio
 */

import { SupabaseProductRepository } from '../../infrastructure/repositories/SupabaseProductRepository';
import { CreateProductUseCase } from '../../application/use-cases/products/CreateProductUseCase';
import { UpdateProductUseCase } from '../../application/use-cases/products/UpdateProductUseCase';
import { DeleteProductUseCase } from '../../application/use-cases/products/DeleteProductUseCase';
import { GetProductsUseCase } from '../../application/use-cases/products/GetProductsUseCase';
import { Product } from '../../domain/entities/Product';

export interface ProductDTO {
    id: string;
    name: string;
    value: number;
    description: string;
    createdAt: Date;
    updatedAt: Date;
}

export class ProductService {
    private repository: SupabaseProductRepository;
    private createUseCase: CreateProductUseCase;
    private updateUseCase: UpdateProductUseCase;
    private deleteUseCase: DeleteProductUseCase;
    private getProductsUseCase: GetProductsUseCase;

    constructor() {
        this.repository = new SupabaseProductRepository();
        this.createUseCase = new CreateProductUseCase(this.repository);
        this.updateUseCase = new UpdateProductUseCase(this.repository);
        this.deleteUseCase = new DeleteProductUseCase(this.repository);
        this.getProductsUseCase = new GetProductsUseCase(this.repository);
    }

    async getAll(organizationId: string): Promise<ProductDTO[]> {
        const products = await this.getProductsUseCase.execute({ organizationId });
        return products.map(this.mapToDTO);
    }

    async create(
        name: string,
        value: number,
        description: string,
        organizationId: string,
        userId?: string
    ): Promise<ProductDTO> {
        const product = await this.createUseCase.execute({
            name,
            value,
            description,
            organizationId,
            userId
        });

        return this.mapToDTO(product);
    }

    async update(
        id: string,
        updates: { name?: string; value?: number; description?: string },
        organizationId: string
    ): Promise<ProductDTO> {
        const product = await this.updateUseCase.execute({
            id,
            ...updates,
            organizationId
        });

        return this.mapToDTO(product);
    }

    async delete(id: string): Promise<void> {
        await this.deleteUseCase.execute({ id });
    }

    private mapToDTO(product: Product): ProductDTO {
        return {
            id: product.id,
            name: product.name,
            value: product.getValue(),
            description: product.description,
            createdAt: product.createdAt,
            updatedAt: product.updatedAt
        };
    }
}

// Singleton instance
export const productService = new ProductService();
